import React, { useEffect, useState } from 'react';
import { io } from "socket.io-client";
import ModernMeetingSetup from './ModernMeetingSetup';
import { PreJoinPageOptions, PreJoinPageParameters } from '../components/miscComponents/PreJoinPage';
import { WelcomePageOptions } from '../components/miscComponents/WelcomePage';
import { checkLimitsAndMakeRequest } from '../methods/utils/checkLimitsAndMakeRequest';

/**
 * Wrapper to make ModernMeetingSetup compatible with ModernMediasfuGeneric
 */
const ModernMeetingSetupWrapper: React.FC<PreJoinPageOptions | WelcomePageOptions> = (options) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!options) return null;
  const credentials = (options as PreJoinPageOptions).credentials;
  const data = credentials ? {
    apiKey: credentials.apiKey,
    apiUserName: credentials.apiUserName,
  } : undefined;

  const handleJoin = async (joinData: {
    meetingID: string;
    userName: string;
    secureCode: string;
    url: string;
    secret: string;
    audioPreference?: string;
    videoPreference?: string;
    audioOutputPreference?: string;
    virtualBackground?: any;
    facingMode?: string;
    isDarkMode?: boolean;
    eventType?: any;
    selfieSegmentation?: any;
  }) => {
    const parameters = options.parameters as PreJoinPageParameters;

    // 1. Show Loading
    parameters.updateIsLoadingModalVisible(true);


    try {
      // 2. Extract Data
      let { meetingID, secret } = joinData;
      const { userName } = joinData;
      let { url } = joinData;

      // Extract preferences
      const {
        audioPreference,
        videoPreference,
        audioOutputPreference,
        virtualBackground,
        facingMode,
        isDarkMode,
        eventType,
        selfieSegmentation,
      } = joinData;

      // Update parameters
      if (audioPreference) parameters.updateAudioPreference?.(audioPreference);
      if (videoPreference) parameters.updateVideoPreference?.(videoPreference);
      if (audioOutputPreference) parameters.updateAudioOutputPreference?.(audioOutputPreference);

      if (typeof isDarkMode === 'boolean') parameters.updateIsDarkMode?.(isDarkMode);

      if (eventType) parameters.updateEventType?.(eventType);

      if (virtualBackground) {
        parameters.updateVirtualBackground?.(virtualBackground);
        parameters.updateKeepBackground?.(true);
        parameters.updateAppliedBackground?.(true);
      }

      if (selfieSegmentation) {
        parameters.updateSelfieSegmentation?.(selfieSegmentation);
      }

      if (facingMode) {
        parameters.updateCurrentFacingMode?.(facingMode);
      }

      // 3. Parse URL: Split at '/meet' and keep only the base URL
      if (url && url.includes('/meet')) {
        const parts = url.split('/meet');
        url = parts[0];
        if (parts.length > 1) {
          // Extract meetingID and secret if available in the URL path
          // Format: LINK/meet/MEETINGID/SECRET
          const pathParts = parts[1].split('/');
          // pathParts[0] is empty string because of leading slash
          if (pathParts.length > 1 && (!meetingID || meetingID.length === 0)) {
            meetingID = pathParts[1];
          }
          if (pathParts.length > 2 && (!secret || secret.length === 0)) {
            secret = pathParts[2];
          }
        }
      }

      // 4. Connect Logic
      if (secret && secret.length > 0) {
        // Update member to handle _2 suffix for host detection
        parameters.updateMember(userName);
        // We have the token, connect directly
        await checkLimitsAndMakeRequest({
          apiUserName: meetingID,
          apiToken: secret,
          link: url,
          userName: userName.includes("_") ? userName.split("_")[0] : userName,
          parameters: parameters,
        });
      } else {
        parameters.updateIsLoadingModalVisible(false);
        setIsLoading(false);
        // Fallback or handle cases without secret if necessary
        // For now, we assume success implies we have what we need
      }
    } catch (e) {
      // Handle errors
      parameters.updateIsLoadingModalVisible(false);
      setIsLoading(false);
      if (parameters.showAlert) {
        parameters.showAlert({
          message: `Failed to join: ${e}`,
          type: 'danger',
          duration: 3000,
        });
      }
    }
  };

  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/meet/')) {
      const parts = path.split('/meet/');
      if (parts.length > 1) {
        const subParts = parts[1].split('/');
        const meetingID = subParts[0];
        const urlSecret = subParts.length > 1 ? subParts[1] : "";

        // Hide the secret from the URL for security
        if (urlSecret && urlSecret.length > 0) {
          window.history.replaceState(null, '', `/meet/${meetingID}`);
        }

        if (meetingID && meetingID.length >= 7 && urlSecret && urlSecret.length > 0) {
           setIsLoading(true);
           const parameters = options.parameters as PreJoinPageParameters;
           
           try {
             // Connect to socket and fetch room details
             const socket = io("/media", { transports: ["websocket"] });
             
             // Set a timeout for connection
             const timeout = setTimeout(() => {
               socket.disconnect();
               setIsLoading(false);
               parameters.showAlert?.({
                 message: "Connection timed out. Please try again.",
                 type: 'danger',
                 duration: 3000,
               });
             }, 15000);
             
             socket.on("connection-success", async () => {
               try {
                 socket.emit("fetchRoom", { sec: urlSecret }, async (response: any) => {
                   clearTimeout(timeout);
                   socket.disconnect();
                   
                   try {
                     if (response && response.success) {
                       // Got room details, now join
                       // Handle _2 suffix for host detection (pem === '0' indicates host)
                       let userName = response.name || "Guest_" + Math.floor(Math.random() * 10000);
                       if (response.pem === '0' && !userName.endsWith("_2")) {
                         userName = userName + "_2";
                       }
                       await handleJoin({
                         meetingID,
                         userName,
                         secureCode: "",
                         url: window.location.origin,
                         secret: urlSecret,
                         audioPreference: response.audioPreference || localStorage.getItem("audioDevice") || undefined,
                         videoPreference: response.videoPreference || localStorage.getItem("videoDevice") || undefined,
                         audioOutputPreference: response.audioOutputPreference || localStorage.getItem("audioOutputDevice") || undefined,
                       });
                     } else {
                       // Room not found or secret expired
                       setIsLoading(false);
                       parameters.showAlert?.({
                         message: "Sorry, the room was not found or the link has expired.",
                         type: 'danger',
                         duration: 3000,
                       });
                     }
                   } catch (joinError) {
                     setIsLoading(false);
                     parameters.showAlert?.({
                       message: `Failed to join room: ${joinError}`,
                       type: 'danger',
                       duration: 3000,
                     });
                   }
                 });
               } catch (emitError) {
                 clearTimeout(timeout);
                 socket.disconnect();
                 setIsLoading(false);
                 parameters.showAlert?.({
                   message: `Failed to fetch room: ${emitError}`,
                   type: 'danger',
                   duration: 3000,
                 });
               }
             });
             
             socket.on("connect_error", (err) => {
               clearTimeout(timeout);
               setIsLoading(false);
               parameters.showAlert?.({
                 message: `Connection error: ${err?.message || 'Unable to connect'}`,
                 type: 'danger',
                 duration: 3000,
               });
             });
           } catch (error) {
             setIsLoading(false);
             parameters.showAlert?.({
               message: `Error: ${error}`,
               type: 'danger',
               duration: 3000,
             });
           }
        }
      }
    }
  }, []);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        background: 'rgba(0, 0, 0, 0.75)', 
        color: 'white',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="sr-only">Loading...</span>
        </div>
        <div>Joining meeting...</div>
        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          .spinner-border { animation: spin 1s linear infinite; border: 4px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; }
        `}</style>
      </div>
    );
  }

  return (
    <ModernMeetingSetup
      useFixedLink={false}
      onJoin={handleJoin}
      data={data}
    />
  );
};

export default ModernMeetingSetupWrapper;
